import {
  ChangeEvent,
  cloneElement,
  FC,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from 'react';
import clsx from 'clsx';
import { Flag, Icon } from '@namespace/icons2';
import { useGetThemeConfig } from '@namespace/themes';
import { propsMerge } from '../../utils/propsMerge';
import { Box } from '../Box';
import { Input } from '../Input';
import { Popover } from '../Popover';
import { SelectContent } from '../Select/components/DesktopSelect/components/SelectContent';
import { useValueReset } from './hooks/useValueReset';
import { PhoneValue } from './types';
import {
  createValue as createValueUtil,
  extractNumber,
  filterByCountryOrCode,
  getCountryByPhoneNumber,
  getMaskByIsoCode,
  getPhoneLength,
  withPlusPrefix
} from './utils';

import styles from './index.module.css';

const MINIMAL_FILTERED_ITEMS_COUNT = 3;

interface CodesListProps {
  size?: 's' | 'm';
  placeholderText?: string;
  text?: string;
}

interface CodeItem {
  countryName: string;
  phoneCode: string;
  iso1Code: string;
}

interface PhoneSearchProps {
  onChange?: (value: PhoneValue) => void;
  onBlur?: () => void;
  value?: PhoneValue;
  codesListItems?: CodeItem[];
  codesListProps?: CodesListProps;
  disabled?: boolean;
  dataRole?: string;
  intent?: 'error' | 'success' | 'default';
  inputSize?: 's' | 'm';
  bottomElement?: ReactNode;
  isFullFieldBorder?: boolean;
  isNeedSearchBorder?: boolean;
  minNumberLength?: number;
  defaultCountry?: string;
}

export const PhoneSearch: FC<PhoneSearchProps> = (props) => {
  const phoneSearchConfig = useGetThemeConfig('PhoneSearch');
  const {
    value: valueProp = { number: '', isoCode: '' },
    onChange,
    onBlur,
    disabled = false,
    codesListItems = [],
    codesListProps = { size: 's', placeholderText: '', text: '' },
    minNumberLength = 0,
    dataRole = '',
    intent = 'default',
    inputSize = 'm',
    bottomElement = null,
    isFullFieldBorder = false,
    isNeedSearchBorder = true,
    disableFlagsSelection = false,
    isNeedContentBorder = false,
    classNames = {},
    ...rest
  } = propsMerge(props, phoneSearchConfig);

  const { iconContainerClassName = '' } = classNames;
  const [showFilter, setShowFilter] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [selectedItem, setSelectedItem] = useState(-1);
  const [isDirty, setIsDirty] = useState(false);

  const code = valueProp.number.slice(0, 3);
  const codesListItemsExists = Boolean(codesListItems.length);

  const countryData = useMemo(() => {
    return (
      getCountryByPhoneNumber(codesListItems, code) || {
        phoneCode: '',
        iso1Code: ''
      }
    );
  }, [code, codesListItems]);

  const mask = useMemo(
    () => getMaskByIsoCode(valueProp.isoCode) ?? null,
    [valueProp.isoCode]
  );

  const filteredCodes = useMemo(() => {
    const filteredItems = codesListItems.filter(
      filterByCountryOrCode(filterValue)
    );
    filteredItems.sort(
      (a: { countryName: string }, b: { countryName: string }) =>
        a.countryName.localeCompare(b.countryName)
    );
    return filteredItems;
  }, [filterValue, codesListItems]);

  const createValue = useMemo(
    () => createValueUtil({ valueProp, codesListItems }),
    [valueProp, codesListItems]
  );

  useValueReset({
    makeValue: createValue,
    onChange,
    valueProp,
    codesListItemsExists
  });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      setIsDirty(true);
      if (showFilter && e.key === 'Tab') {
        setShowFilter(false);
      }
    },
    [showFilter]
  );

  const handleFlagClick = useCallback(() => {
    if (disableFlagsSelection) {
      return;
    }

    setShowFilter((prev) => !prev);
  }, [setShowFilter, disableFlagsSelection]);

  const handleNumberChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value.length < minNumberLength) {
        return;
      }
      const extractedNumber = extractNumber(value);
      if (extractedNumber !== valueProp.number) {
        onChange?.(createValue(extractedNumber, valueProp.isoCode));
      }
    },
    [
      onChange,
      createValue,
      minNumberLength,
      valueProp.number,
      valueProp.isoCode
    ]
  );

  const closePopover = useCallback(() => {
    setFilterValue('');
    setShowFilter(false);
  }, []);

  const handlePopoverClose = useCallback(() => {
    closePopover();
  }, [closePopover]);

  const handleISOCodeChange = useCallback(
    (option: unknown) => {
      const { iso1Code } = option as CodeItem;
      onChange?.(createValue(null, iso1Code));
    },
    [onChange, createValue]
  );

  const handleNumberInputClick = useCallback(() => {
    setIsDirty(true);
    if (showFilter) {
      closePopover();
    }
  }, [closePopover, showFilter]);

  const maxLength = useMemo(() => {
    return getMaskByIsoCode(valueProp.isoCode)
      ? undefined
      : getPhoneLength(valueProp.isoCode, countryData.phoneCode);
  }, [valueProp.isoCode, countryData.phoneCode]);

  const wrapperClasses = [styles.general];
  if (isDirty) {
    wrapperClasses.push(styles[intent]);
  }

  const displaySearchInput =
    filteredCodes.length >= MINIMAL_FILTERED_ITEMS_COUNT ||
    Boolean(currentValue);

  const handleFilter = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.value);
    setTimeout(() => {
      setFilterValue(event.target.value);
    });
  }, []);

  return (
    <Box direction="column" onKeyDown={handleKeyDown} data-role={dataRole}>
      <Popover
        trigger={
          <Box
            className={clsx([
              wrapperClasses,
              isFullFieldBorder && styles.fullBorder
            ])}
            data-role={dataRole && `${dataRole}-phone-input`}
          >
            <Box
              align="center"
              onClick={handleFlagClick}
              className={clsx([
                styles.flagWrapper,
                iconContainerClassName,
                disabled && styles.disabled
              ])}
              data-role={dataRole && `${dataRole}-flag`}
            >
              <Box className={styles.flagContainer}>
                {valueProp.isoCode && (
                  <Flag
                    data-testid="flag"
                    className={styles.icon}
                    size="s"
                    iso={valueProp.isoCode}
                  />
                )}
              </Box>
              <Icon
                size="xs"
                name="icons/general/nav/dropdown_down"
                className={clsx(styles.arrowDown, showFilter && styles.arrowUp)}
              />
            </Box>
            <Input
              data-role={`${dataRole}-phone-number`}
              type="tel"
              name="number"
              className={styles.inputNumber}
              onChange={handleNumberChange}
              onClick={handleNumberInputClick}
              value={withPlusPrefix(valueProp.number)}
              onBlur={onBlur}
              min={0}
              maxLength={maxLength}
              mask={mask}
              size={inputSize}
              autoComplete="nope"
              disabled={disabled}
              isDisabledBottomElement={true}
              {...rest}
            />
          </Box>
        }
        content={
          <>
            {displaySearchInput && (
              <Box
                align="center"
                className={clsx([
                  styles.countryCodeInput,
                  !isNeedSearchBorder && styles.noBorder,
                  isNeedContentBorder && styles.noRoundedInput
                ])}
              >
                <Icon
                  className={styles.searchIcon}
                  size="s"
                  name="icons/general/action/search"
                />
                <Input
                  type="text"
                  intent="ghost"
                  name="codeSearchInput"
                  className={styles.inputPlaceholder}
                  onChange={handleFilter}
                  placeholder={codesListProps?.placeholderText}
                  value={currentValue}
                  maxLength={50}
                  min={0}
                  size={inputSize}
                  autoComplete="nope"
                />
              </Box>
            )}
            <SelectContent
              dataRole={dataRole}
              options={filteredCodes}
              onClick={handleISOCodeChange}
              value={valueProp.isoCode}
              setIsOpen={setShowFilter}
              setCurrentValue={setFilterValue}
              hasCheck={true}
              emptyText={codesListProps.text}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              size="s"
            />
          </>
        }
        classNames={{
          contentClass: clsx([
            styles.contentClass,
            styles[inputSize],
            isNeedContentBorder && styles.contentBorder
          ])
        }}
        setIsOpenExternal={handlePopoverClose}
        isOpenExternal={showFilter}
        disabledToggleInWrapper={true}
      />
      {bottomElement &&
        !showFilter &&
        cloneElement(bottomElement, { className: styles.borderBottomCustom })}
    </Box>
  );
};
